import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import { X, User, Lock, Loader2, CheckCircle, Camera } from 'lucide-react';
import LogoPlaceholder from '../ui/LogoPlaceholder';
import { adminApplicationAPI } from '../../services/api';

export function AdminApplicationForm({ onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    fullname: '',
    gender: 'M',
    username: '',
    password: '',
    confirmPassword: '',
    avatar: null
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const fileInputRef = useRef(null);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleAvatarChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        setErrors(prev => ({ ...prev, avatar: 'Image file is too large. Please select an image smaller than 5MB.' }));
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({ ...prev, avatar: 'Please select a valid image file.' }));
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Create canvas to resize/compress image
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Resize if image is too large (max 400x400)
          const maxDimension = 400;
          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = (height / width) * maxDimension;
              width = maxDimension;
            } else {
              width = (width / height) * maxDimension;
              height = maxDimension;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to base64 with compression
          const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.8);
          
          setFormData(prev => ({
            ...prev,
            avatar: compressedDataUrl
          }));
          
          // Clear any avatar errors
          if (errors.avatar) {
            setErrors(prev => {
              const newErrors = { ...prev };
              delete newErrors.avatar;
              return newErrors;
            });
          }
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Fullname validation
    if (!formData.fullname || !formData.fullname.trim()) {
      newErrors.fullname = 'Full name is required';
    }

    // Username validation
    if (!formData.username || !formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters long';
    }

    // Password validation
    if (!formData.password || !formData.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters long';
    }

    // Password confirmation
    if (!formData.confirmPassword || formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const applicationData = {
        fullname: formData.fullname.trim(),
        gender: formData.gender,
        username: formData.username.trim(),
        password: formData.password,
        avatar: formData.avatar
      };

      const response = await adminApplicationAPI.apply(applicationData);

      if (response.success) {
        setIsSuccess(true);
        setSuccessMessage(response.message);
        
        // Call onSuccess callback if provided
        if (onSuccess) {
          onSuccess(response);
        }

        // Auto-close after 3 seconds
        setTimeout(() => {
          onClose();
        }, 3000);
      }
    } catch (error) {
      setErrors({ general: error.message || 'Failed to submit application. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  // If successful, show success message
  if (isSuccess) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm animate-fade-in">
        <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl transform transition-all duration-300 animate-scale-in">
          <Card className="border-0 shadow-none">
            <CardContent className="pt-8 pb-6 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-green-800 mb-2">Application Submitted!</h3>
              <p className="text-gray-600 mb-6">{successMessage}</p>
              <Button 
                onClick={onClose}
                className="bg-green-600 hover:bg-green-700"
              >
                Close
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm animate-fade-in overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl transform transition-all duration-300 animate-scale-in my-8">
        <Card className="border-0 shadow-none">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <LogoPlaceholder size="medium" showText={false} className="" />
                <CardTitle className="text-green-800">Apply as Admin</CardTitle>
              </div>
              <Button 
                variant="ghost" 
                onClick={onClose} 
                className="text-gray-500 hover:text-gray-700 rounded-full h-8 w-8 p-0 transition-all duration-200 hover:bg-gray-100"
                disabled={isLoading}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Information notice */}
              <Alert className="border-blue-200 bg-blue-50">
                <AlertDescription className="text-blue-800 text-sm">
                  Your application will be reviewed by existing administrators. You will be notified once your application is processed.
                </AlertDescription>
              </Alert>

              {/* General error message */}
              {errors.general && (
                <Alert className="border-red-200 bg-red-50">
                  <AlertDescription className="text-red-700">{errors.general}</AlertDescription>
                </Alert>
              )}

              {/* Avatar Upload */}
              <div className="space-y-2">
                <Label>Profile Picture (Optional)</Label>
                <div className="flex items-center gap-4">
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className="cursor-pointer"
                  >
                    {formData.avatar ? (
                      <img
                        src={formData.avatar}
                        alt="Avatar preview"
                        className="w-24 h-24 rounded-full object-cover border-4 border-green-200"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white text-2xl font-bold border-4 border-green-200">
                        <Camera className="w-8 h-8" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleAvatarChange}
                      accept="image/*"
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isLoading}
                      className="mb-2"
                    >
                      {formData.avatar ? 'Change Picture' : 'Upload Picture'}
                    </Button>
                    <p className="text-xs text-gray-500">
                      Optional. Max 5MB, will be resized to 400x400.
                      <br />You can also add this later in your profile.
                    </p>
                    {errors.avatar && <p className="text-red-500 text-sm mt-1">{errors.avatar}</p>}
                  </div>
                </div>
              </div>

              {/* Personal Information */}
              <div className="space-y-4">
                <h3 className="text-green-800 border-b pb-2 font-semibold">Personal Information</h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="fullname">Full Name *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        id="fullname"
                        value={formData.fullname}
                        onChange={(e) => handleInputChange('fullname', e.target.value)}
                        placeholder="Enter your full name"
                        className="pl-10 rounded-xl transition-all duration-200 focus:ring-2 focus:ring-green-500"
                        required
                        disabled={isLoading}
                      />
                    </div>
                    {errors.fullname && <p className="text-red-500 text-sm">{errors.fullname}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="gender">Gender *</Label>
                    <Select 
                      value={formData.gender}
                      onValueChange={(value) => handleInputChange('gender', value)}
                      disabled={isLoading}
                    >
                      <SelectTrigger className="rounded-xl transition-all duration-200 focus:ring-2 focus:ring-green-500">
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="M">Male</SelectItem>
                        <SelectItem value="F">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Account Credentials */}
              <div className="space-y-4">
                <h3 className="text-green-800 border-b pb-2 font-semibold">Account Credentials</h3>
                
                <div className="space-y-2">
                  <Label htmlFor="username">Username *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      id="username"
                      value={formData.username}
                      onChange={(e) => handleInputChange('username', e.target.value)}
                      placeholder="Choose a unique username"
                      className="pl-10 rounded-xl transition-all duration-200 focus:ring-2 focus:ring-green-500"
                      required
                      disabled={isLoading}
                    />
                  </div>
                  {errors.username && <p className="text-red-500 text-sm">{errors.username}</p>}
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="password">Password *</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        id="password"
                        type="password"
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        placeholder="At least 6 characters"
                        className="pl-10 rounded-xl transition-all duration-200 focus:ring-2 focus:ring-green-500"
                        required
                        disabled={isLoading}
                      />
                    </div>
                    {errors.password && <p className="text-red-500 text-sm">{errors.password}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password *</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        id="confirmPassword"
                        type="password"
                        value={formData.confirmPassword}
                        onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                        placeholder="Re-enter your password"
                        className="pl-10 rounded-xl transition-all duration-200 focus:ring-2 focus:ring-green-500"
                        required
                        disabled={isLoading}
                      />
                    </div>
                    {errors.confirmPassword && <p className="text-red-500 text-sm">{errors.confirmPassword}</p>}
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Button 
                  type="submit" 
                  className="flex-1 bg-green-600 hover:bg-green-700 flex items-center justify-center gap-2"
                  disabled={isLoading}
                >
                  {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                  {isLoading ? 'Submitting...' : 'Submit Application'}
                </Button>
                <Button 
                  type="button"
                  variant="outline" 
                  onClick={onClose}
                  className="flex-1"
                  disabled={isLoading}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
